import { computed, effect, inject, Injectable } from '@angular/core';
import { asNonUndefined, WINDOW } from '@kaseya-utils';
import { MedalliaState } from '@core/medallia/store/states/medallia.state';
import { Store } from '@ngxs/store';
import { LoadMedalliaInfo } from '@core/medallia/store/actions/medallia.actions';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { MedalliaWindow, userRoleToMedalliaUserRole } from '@core/medallia/models/medallia.interface';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@kaseya-shared';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class MedalliaFacade {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly store = inject(Store);
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly window = inject<MedalliaWindow>(WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly medalliaSurveyFormId = 1407;
  private readonly medalliaSurveyScript = 'https://resources.digital-cloud-smf1.medallia.com/websites/13113/onsite/embed.js';
  private readonly mappedUserRole = computed(() => userRoleToMedalliaUserRole[asNonUndefined(this.userInfoFacade.userRole())]);
  private readonly hasMedalliaIntegrationView = this.permissionsFacade.hasPermission(Permission.MedalliaIntegrationView);

  private medalliaScriptElement?: HTMLScriptElement;

  readonly hashedEmail = this.store.selectSignal(MedalliaState.hashedEmail);
  readonly isLoggedIn = toSignal(this.authorizationService.onLogin);

  constructor() {
    this.loadMedalliaDataOnLogin();
    this.populateDataOnLogin();
    this.authorizationService.onLogout.subscribe(() => this.removeScript());
  }

  loadMedalliaDataOnLogin(): void {
    effect(() => {
      if (this.isLoggedIn() && this.hasMedalliaIntegrationView()) {
        this.loadMedalliaScript();
        this.loadMedalliaInfo();
      }
    });
  }

  populateDataOnLogin(): void {
    effect(() => {
      const hashedEmail = this.hashedEmail();
      if (hashedEmail) {
        this.setDataForSurvey(hashedEmail);
      }
    });
  }

  showMedalliaSurvey(): void {
    this.window.KAMPYLE_ONSITE_SDK.showForm(this.medalliaSurveyFormId);
  }

  removeScript(): void {
    if (this.medalliaScriptElement) {
      this.medalliaScriptElement.remove();
      this.medalliaScriptElement = undefined;
    }
  }

  private loadMedalliaScript(): void {
    this.medalliaScriptElement = this.document.createElement('script');
    this.medalliaScriptElement.src = this.medalliaSurveyScript;
    this.medalliaScriptElement.async = true;
    this.medalliaScriptElement.onerror = (): void => this.removeScript();
    this.document.head.appendChild(this.medalliaScriptElement);
  }

  private setDataForSurvey(hashedEmail: string): void {
    this.window.dataLayer = [
      {
        username: hashedEmail,
        role: this.mappedUserRole(),
        salesforceID: this.userInfoFacade.salesforceId(),
      },
    ];
  }
  private loadMedalliaInfo(): void {
    this.store.dispatch(new LoadMedalliaInfo());
  }
}
